import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight, Zap, BookOpen, Lightbulb, Compass } from 'lucide-react';

/**
 * DynamicCTA Component - Premium Call-to-Action for Articles & Sidebar
 * 
 * Features:
 * - Context-aware messaging based on article category
 * - Animated gradient border
 * - Multiple variants: sidebar, inline, featured
 * - Links to relevant category hub or main trial page
 */
export const DynamicCTA = ({
    article,
    variant = 'sidebar', // 'sidebar', 'inline', 'featured'
    showExplore = true
}) => {
    const category = article?.category || 'Setup Guides';

    // Dynamic content based on category
    const ctaConfig = {
        'Setup Guides': {
            icon: BookOpen,
            headline: 'Master IPTV Setup',
            subtext: 'Complete step-by-step guides for all devices',
            exploreText: 'Explore All Guides',
            explorePath: '/blog/category/setup-guides',
            ctaText: 'Get Free Trial',
            ctaPath: 'https://xtreamcube.com/freetrialiptv/',
            gradient: 'from-emerald-500/20 via-primary/10 to-transparent'
        },
        'Regional IPTV': {
            icon: Compass,
            headline: 'IPTV in Your Region',
            subtext: 'Find the best service for your location',
            exploreText: 'All Regional Guides',
            explorePath: '/blog/category/regional-iptv',
            ctaText: 'Check Availability',
            ctaPath: 'https://xtreamcube.com/freetrialiptv/',
            gradient: 'from-blue-500/20 via-primary/10 to-transparent'
        },
        'Reviews': {
            icon: Sparkles,
            headline: 'Compare IPTV Services',
            subtext: 'Honest reviews from real experts',
            exploreText: 'All Reviews',
            explorePath: '/blog/category/reviews',
            ctaText: 'Try Top Rated',
            ctaPath: 'https://xtreamcube.com/freetrialiptv/',
            gradient: 'from-yellow-500/20 via-primary/10 to-transparent'
        },
        'Featured': {
            icon: Lightbulb,
            headline: 'IPTV Essentials',
            subtext: 'Everything you need to know about IPTV',
            exploreText: 'Featured Articles',
            explorePath: '/blog/category/featured',
            ctaText: 'Start Free Trial',
            ctaPath: 'https://xtreamcube.com/freetrialiptv/',
            gradient: 'from-purple-500/20 via-primary/10 to-transparent'
        },
        'Troubleshooting': {
            icon: Zap,
            headline: 'Fix Streaming Issues',
            subtext: 'Get back to watching in minutes',
            exploreText: 'View Solutions',
            explorePath: '/blog/category/troubleshooting',
            ctaText: 'Get 24/7 Support',
            ctaPath: 'https://xtreamcube.com/support/',
            gradient: 'from-red-500/20 via-primary/10 to-transparent'
        }
    };

    const config = ctaConfig[category] || ctaConfig['Setup Guides'];
    const Icon = config.icon;

    // Slim variant (Horizontal, compact banner)
    if (variant === 'slim') {
        return (
            <motion.div
                initial={{ opacity: 0, y: 5 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className={`relative overflow-hidden rounded-xl border border-white/10 bg-gradient-to-r ${config.gradient} p-4 mb-8`}
            >
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3 w-full sm:w-auto">
                        <div className="p-2 bg-primary/20 rounded-lg shrink-0">
                            <Icon className="w-4 h-4 text-primary" />
                        </div>
                        <div className="min-w-0">
                            <h3 className="font-bold text-sm truncate">{config.headline}</h3>
                            <p className="text-xs text-muted-foreground truncate">{config.subtext}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 w-full sm:w-auto shrink-0">
                        <a
                            href={config.ctaPath}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-semibold text-xs whitespace-nowrap hover:bg-primary/90 transition-all group"
                        >
                            {config.ctaText}
                            <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                        </a>
                        {showExplore && (
                            <Link
                                to={config.explorePath}
                                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs font-medium whitespace-nowrap transition-colors"
                            >
                                {config.exploreText}
                            </Link>
                        )}
                    </div>
                </div>
            </motion.div>
        );
    }

    // Sidebar variant (compact)
    if (variant === 'sidebar') {
        return (
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
                className="glass rounded-xl overflow-hidden border border-white/10"
            >
                {/* Gradient top accent */}
                <div className={`h-1 bg-gradient-to-r ${config.gradient}`} />

                <div className="p-5">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-primary/20 rounded-lg">
                            <Icon className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                            <h3 className="font-bold text-sm">{config.headline}</h3>
                            <p className="text-xs text-muted-foreground">{config.subtext}</p>
                        </div>
                    </div>

                    {/* CTA Buttons */}
                    <div className="space-y-2">
                        <a
                            href={config.ctaPath}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 w-full p-3 bg-primary text-primary-foreground rounded-lg font-semibold text-sm hover:bg-primary/90 transition-all group"
                        >
                            <Zap className="w-4 h-4" />
                            {config.ctaText}
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </a>

                        {showExplore && (
                            <Link
                                to={config.explorePath}
                                className="flex items-center justify-center gap-2 w-full p-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-sm font-medium transition-colors"
                            >
                                {config.exploreText}
                            </Link>
                        )}
                    </div>
                </div>
            </motion.div>
        );
    }

    // Inline variant (for within article content)
    if (variant === 'inline') {
        return (
            <div className={`my-8 p-6 rounded-2xl bg-gradient-to-r ${config.gradient} border border-primary/20`}>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-primary/20 rounded-xl">
                            <Icon className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                            <h4 className="font-bold">{config.headline}</h4>
                            <p className="text-sm text-muted-foreground">{config.subtext}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <a
                            href={config.ctaPath}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold text-sm hover:bg-primary/90 transition-all group"
                        >
                            {config.ctaText}
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </a>
                        {showExplore && (
                            <Link
                                to={config.explorePath}
                                className="text-sm font-medium text-primary hover:underline"
                            >
                                {config.exploreText}
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Featured variant (for bottom of article)
    if (variant === 'featured') {
        return (
            <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="my-12 relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/20 via-background to-card/50 border border-primary/30 p-8"
            >
                {/* Background decorations */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />

                <div className="relative z-10">
                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                        <div className="flex items-start gap-4">
                            <div className="p-3 bg-primary/20 rounded-xl">
                                <Sparkles className="w-8 h-8 text-primary" />
                            </div>
                            <div>
                                <h3 className="text-2xl font-bold mb-1">Ready to Experience Premium IPTV?</h3>
                                <p className="text-muted-foreground">
                                    Join 27,500+ satisfied customers streaming in 4K quality with 24/7 support.
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
                            <a
                                href="https://xtreamcube.com/freetrialiptv/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-bold hover:bg-primary/90 transition-all group"
                            >
                                <Zap className="w-5 h-5" />
                                Get Free 24H Trial
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </a>

                            <Link
                                to="/blog"
                                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg font-medium transition-colors"
                            >
                                Learn More
                            </Link>
                        </div>
                    </div>
                </div>
            </motion.section>
        );
    }

    return null;
};

export default DynamicCTA;
